import classNames from 'classnames';
import CurrencyFormat from 'components/CurrencyFormat';
import DurationFormat from 'components/DurationFormat';
import { DiamondPattern } from 'components/HeaderBackgrounds';
import config from 'config';
import React from 'react';
import type { ReportDraft, ReportQuery } from 'redux/modules/superreports.types';
import type { Account } from 'types/account';
import { isContrastReadable } from 'utils/colors';
import { getTimeSpanWithSmartYear } from 'utils/date-helper';
import type { CostAndDurationSummary } from 'utils/summary';
import { getContentStyle } from 'utils/superreports';
import styles from './PublicReportHeader.module.scss';

interface PublicReportHeaderProps {
  account: Account;
  draft: ReportDraft;
  minimumWidgetWidth: number;
  query: ReportQuery;
  showMoney?: boolean;
  totals: CostAndDurationSummary;
}

const PublicReportHeader: React.FC<PublicReportHeaderProps> = ({
  account,
  draft,
  minimumWidgetWidth,
  query,
  showMoney: canShowMoney = true,
  totals,
}) => {
  const timespan = getTimeSpanWithSmartYear({
    from: query.from,
    to: query.to,
  });

  const accountColor = account.color ? `#${account.color}` : null;
  const needsDarkText = accountColor && !isContrastReadable('#fff', accountColor);

  const showDuration = totals.minutesLogged > 0;
  const showMoney = canShowMoney && totals.costLogged > 0;
  const showTotals = showDuration || showMoney;
  const contentStyle = getContentStyle(minimumWidgetWidth);

  return (
    <header className={styles.container}>
      <DiamondPattern className={styles.backgroundPattern} colorStart={accountColor} />
      <div
        className={classNames(styles.hero, {
          [styles.withDescription]: !!draft.description,
          [styles.darkTextHero]: needsDarkText,
        })}
      >
        <div className={styles.topLeft}>
          Created with <a href={config.publicReportLinkBackUrl}>Timely</a>
        </div>
        <div className={styles.mainContent}>
          <picture>
            <img src={account.logo.medium_retina} alt="logo" />
          </picture>
          <div
            className={classNames(styles.accountDetails, {
              [styles.accountDetailsDarkText]: needsDarkText,
            })}
          >
            {account.name}
          </div>
          <h1>{draft.name || 'Timesheet Report'}</h1>
          <h2>{draft.description}</h2>
        </div>
        <div className={styles.statisticsBlock} style={contentStyle}>
          <div>
            <h5>Report Period</h5>
            <h3 data-testid="report-period-publicreportheader">{timespan}</h3>
          </div>
          {showTotals && (
            <div>
              <h5>Total Logged</h5>
              <h3>
                {showDuration && (
                  <DurationFormat
                    format="h [hours] mm [minutes]"
                    value={totals.minutesLogged}
                    animated={false}
                  />
                )}
                {showMoney && (
                  <span className={styles.muted}>
                    {showDuration && ' / '}
                    <CurrencyFormat
                      value={totals.costLogged}
                      animated={false}
                      currency={totals.currency}
                    />
                  </span>
                )}
              </h3>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default PublicReportHeader;
